// src/models/firebaseCollectionSchema.ts
/**
* Define a Firestore collection with optional nested subcollections.
*
* This function creates a fully-typed collection definition that includes:
* - Schema validation
* - Automatic type derivation (Type, Document, Id)
* - Support for nested subcollections
*
* Path resolution for subcollections is handled by SubCollectionRepository,
* which takes a parent path prefix and appends the subcollection name.
*
* @param name - Collection name (e.g., "classes") or subcollection segment (e.g., "registrants")
* @param schema - Zod schema with required `id` field (must be FirebaseCollectionSchema)
* @param subcollections - Optional map of subcollection name → collection definition
* @param collectionOptions - Optional flags (e.g. {@link FirestoreCollectionOptions.dwReplication})
* @returns Fully-typed collection definition
*
* @example
* ```typescript
* // Simple collection
* export const Classes = defineFirestoreCollection(
*   "classes",
*   z.object({
*     id: zodFlavorId<ClassId>(),
*     name: z.string(),
*   })
* );
*
* // Collection with subcollections
* export const ClassInstanceWebinars = defineFirestoreCollection(
*   "class_instance_webinars",
*   classInstanceWebinarSchema,
*   {
*     registrants: defineFirestoreCollection(
*       "registrants",
*       webinarRegistrantSchema
*     ),
*   }
* );
* ```
*/
function defineFirestoreCollection(name, schema, subcollections, collectionOptions) {
	const zodSchema = schema;
	return {
		name,
		schema,
		subcollections,
		dwReplication: collectionOptions?.dwReplication === true,
		exportDocumentBodyId: collectionOptions?.exportDocumentBodyId === true,
		firestoreCollectionPath: collectionOptions?.firestoreCollectionPath,
		executionTrace: collectionOptions?.executionTrace === true,
		Type: {},
		DocumentType: {},
		IdType: {},
		getPath: () => name,
		parse: (data) => zodSchema.parse(data),
		safeParse: (data) => zodSchema.safeParse(data)
	};
}

export { defineFirestoreCollection };
//# sourceMappingURL=firebaseCollectionSchema.mjs.map